import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownUp, ArrowDownToLine, ArrowUpFromLine, History } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { listStockMovements } from "@/lib/admin/inventory.functions";
import { MOVEMENT_REASON_LABEL, type StockMovement } from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/stock-movement")({
  head: () => ({
    meta: [
      { title: "Stock Movement — Admin" },
      {
        name: "description",
        content:
          "Complete stock movement history: previous stock, movement, balance, reason, reference and user.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StockMovementPage,
});

const WINDOWS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
];

function StockMovementPage() {
  const movesFn = useServerFn(listStockMovements);
  const [days, setDays] = useState(30);

  const movements = useQuery({
    queryKey: ["admin", "inventory", "movements", days],
    queryFn: () => movesFn({ data: { days } as never }),
    staleTime: 15_000,
  });

  const rows = useMemo(() => movements.data ?? [], [movements.data]);
  const kpi = useMemo(() => {
    const inbound = rows.filter((r) => r.quantity >= 0);
    const outbound = rows.filter((r) => r.quantity < 0);
    return {
      total: rows.length,
      inbound: inbound.reduce((s, r) => s + r.value_paise, 0),
      outbound: outbound.reduce((s, r) => s + r.value_paise, 0),
      items: new Set(rows.map((r) => r.item_name)).size,
    };
  }, [rows]);

  const columns: Array<DataColumn<StockMovement>> = [
    {
      key: "date",
      header: "Date",
      render: (r) => r.movement_date,
      sortValue: (r) => r.movement_date,
      exportValue: (r) => r.movement_date,
    },
    {
      key: "item",
      header: "Ingredient",
      render: (r) => <span className="font-medium text-foreground">{r.item_name}</span>,
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "prev",
      header: "Previous stock",
      align: "right",
      render: (r) => `${r.previous_stock} ${r.unit}`,
      sortValue: (r) => r.previous_stock,
      exportValue: (r) => r.previous_stock,
    },
    {
      key: "move",
      header: "Movement",
      align: "right",
      render: (r) => (
        <span className={r.quantity < 0 ? "text-destructive" : "text-foreground"}>
          {r.quantity > 0 ? "+" : ""}
          {r.quantity} {r.unit}
        </span>
      ),
      sortValue: (r) => r.quantity,
      exportValue: (r) => r.quantity,
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (r) => `${r.balance_stock} ${r.unit}`,
      sortValue: (r) => r.balance_stock,
      exportValue: (r) => r.balance_stock,
    },
    {
      key: "value",
      header: "Value",
      align: "right",
      render: (r) => inr(r.value_paise / 100),
      sortValue: (r) => r.value_paise,
      exportValue: (r) => r.value_paise / 100,
    },
    {
      key: "reason",
      header: "Reason",
      render: (r) => (
        <Badge variant={r.quantity < 0 ? "outline" : "secondary"}>
          {MOVEMENT_REASON_LABEL[r.reason] ?? r.reason}
        </Badge>
      ),
      exportValue: (r) => MOVEMENT_REASON_LABEL[r.reason] ?? r.reason,
    },
    {
      key: "ref",
      header: "Reference",
      render: (r) => <span className="font-mono text-[11px]">{r.reference || "—"}</span>,
      exportValue: (r) => r.reference,
    },
    {
      key: "user",
      header: "User",
      render: (r) => r.performed_by,
      sortValue: (r) => r.performed_by,
      exportValue: (r) => r.performed_by,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Stock Movement"
        description="Every inbound and outbound movement, with running balances and the user who made the entry."
      />
      <RefreshBar
        targets={[{ key: "inventory", label: "movements" }]}
        busy={movements.isFetching}
        lastUpdated={movements.dataUpdatedAt}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Movements"
          value={num(kpi.total)}
          tone="info"
          icon={History}
          loading={movements.isPending}
        />
        <KpiCard
          label="Inbound value"
          value={inr(kpi.inbound / 100)}
          tone="success"
          icon={ArrowDownToLine}
          loading={movements.isPending}
        />
        <KpiCard
          label="Outbound value"
          value={inr(kpi.outbound / 100)}
          tone="danger"
          icon={ArrowUpFromLine}
          loading={movements.isPending}
        />
        <KpiCard
          label="Ingredients touched"
          value={num(kpi.items)}
          tone="teal"
          icon={ArrowDownUp}
          loading={movements.isPending}
        />
      </div>

      <Panel title="Movement history" subtitle={`${rows.length} entries`}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {WINDOWS.map((w) => (
            <Button
              key={w.days}
              size="sm"
              variant={days === w.days ? "default" : "outline"}
              onClick={() => setDays(w.days)}
            >
              {w.label}
            </Button>
          ))}
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={movements.isPending}
          exportName="stock-movements"
          importModule="stock-movements"
          pageSize={20}
          minWidth={1180}
          searchOn={(r) => `${r.item_name} ${r.reason} ${r.reference} ${r.performed_by}`}
        />
      </Panel>
    </div>
  );
}
